// =============================================================================
// domain/customSeries.ts — trades → the points a custom widget plots.
// -----------------------------------------------------------------------------
// Buckets by day, week or month, runs each chosen metric over each bucket, and
// optionally splits long from short.
//
// ── 🚨 "CUMULATIVE" ACCUMULATES THE TRADES, NOT THE VALUES ──
//
// This is the subtlety in the whole file, and getting it backwards produces a
// chart that looks plausible and means nothing.
//
//   right   period 3's value = the metric computed over periods 1+2+3
//   wrong   period 3's value = period1 + period2 + period3
//
// For Net P&L the two agree, which is exactly why the mistake survives review —
// the equity curve looks correct. For **Win rate** they could not be more
// different: the right answer is "your win rate over everything so far", the
// wrong one is a sum of percentages that passes 300% by March. Same for profit
// factor, every average, and max drawdown.
//
// ── ⚠ WEEKS START ON MONDAY ──
//
// `(getDay() + 6) % 7`, matching the old client. A Sunday-start week would move
// every Sunday trade into the previous bucket and quietly disagree with every
// weekly figure elsewhere in the app.
//
// ── ⚠ BUCKETED ON THE EFFECTIVE DATE (D130) ──
//
// The session date under session mode, the calendar date otherwise — the same
// rule every other dated surface follows. An evening trade belongs to the
// session it was part of, not to the calendar page it landed on.
// =============================================================================

import { effectiveTradeDate } from './session';
import type { DayBoundaryMode } from './range';
import type { CustomMetric, MetricTrade } from './customMetrics';

export type GroupBy = 'day' | 'week' | 'month';

export interface PeriodBucket {
  /** The sort key: an ISO date, or `YYYY-MM` for months. */
  key: string;
  /** What the axis shows: `Jul 13`, `Jul 25`. */
  label: string;
  trades: MetricTrade[];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number): string => String(n).padStart(2, '0');

/** ⚠ Local midnight, as everywhere in this client. */
function parseIso(iso: string): Date | null {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function bucketOf(iso: string, groupBy: GroupBy): { key: string; label: string } | null {
  const d = parseIso(iso);
  if (!d) return null;
  if (groupBy === 'month') {
    return { key: iso.slice(0, 7), label: `${MONTHS[d.getMonth()]} ’${String(d.getFullYear()).slice(2)}` };
  }
  if (groupBy === 'week') {
    // ⚠ MONDAY. See the header.
    const x = new Date(d);
    x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
    return {
      key: `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`,
      label: `${MONTHS[x.getMonth()]} ${x.getDate()}`,
    };
  }
  return { key: iso, label: `${MONTHS[d.getMonth()]} ${d.getDate()}` };
}

/**
 * Trades → ordered buckets.
 *
 * ⚠ ONLY PERIODS THAT HAVE TRADES, matching the old client. A gap is a gap: the
 * axis compresses rather than drawing a run of zeros through weekends and
 * holidays, which would read as a losing streak on a cumulative chart.
 */
export function groupTradesByPeriod(
  trades: readonly MetricTrade[], groupBy: GroupBy, mode: DayBoundaryMode,
): PeriodBucket[] {
  const map = new Map<string, PeriodBucket>();
  for (const t of trades) {
    const iso = effectiveTradeDate(t, mode);
    if (!iso) continue;
    const b = bucketOf(iso, groupBy);
    if (!b) continue;
    const cur = map.get(b.key);
    if (cur) cur.trades.push(t);
    else map.set(b.key, { key: b.key, label: b.label, trades: [t] });
  }
  return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
}

/** One plotted point. Keys are metric ids, or `long`/`short` in split mode. */
export interface SeriesPoint {
  label: string;
  values: Record<string, number | null>;
}

export interface SeriesDef {
  key: string;
  label: string;
  color: string;
  unit: string;
  chart: ChartType;
}

export type ChartType = 'line' | 'area' | 'bar';

/**
 * The old client's default series colours, in order.
 *
 * ⚠ SIX, AND THE EXACT SIX. An earlier version of this file carried four
 * plausible-looking ones — `#f59e0b` for `#f5b342`, `#10b981` for `#22d3ee` —
 * and nothing would have caught it: the overlay caps at four metrics, so the
 * last two never render, and the first two were right. A third series would
 * simply have been the wrong amber, and a fourth green instead of cyan.
 */
export const CW_PALETTE = [
  '#2e9dff', '#a855f7', '#f5b342', '#22d3ee', '#f97316', '#34d399',
] as const;
/** 🚨 FIXED in split mode — a long/short pair must read the same on every widget. */
export const LONG_COLOR = '#2e9dff';
export const SHORT_COLOR = '#a855f7';

export interface SeriesInput {
  trades: readonly MetricTrade[];
  metrics: readonly CustomMetric[];
  groupBy: GroupBy;
  cumulative: boolean;
  /** Requested. Whether it APPLIES is decided here — see `splitApplies`. */
  directional: boolean;
  chartTypes: readonly ChartType[];
  colors?: readonly string[] | undefined;
  mode: DayBoundaryMode;
}

/**
 * ⚠ THE SPLIT APPLIES ONLY TO A SINGLE DIRECTIONAL METRIC, and the check lives
 * here rather than only in the UI. A stored widget can carry `directional: true`
 * next to a metric that has since lost the flag, or next to an overlay — and a
 * split that silently means nothing is worse than a toggle that is missing.
 */
export function splitApplies(
  metrics: readonly CustomMetric[], directional: boolean,
): boolean {
  return directional && metrics.length === 1 && !!metrics[0]?.directional;
}

export function buildCustomSeries(
  input: SeriesInput,
): { points: SeriesPoint[]; series: SeriesDef[] } {
  const { trades, metrics, groupBy, cumulative, chartTypes, colors, mode } = input;
  const buckets = groupTradesByPeriod(trades, groupBy, mode);
  const split = splitApplies(metrics, input.directional);
  const chartAt = (i: number): ChartType => chartTypes[i] ?? 'line';

  const points: SeriesPoint[] = [];
  let acc: MetricTrade[] = [];
  for (const b of buckets) {
    // 🚨 THE TRADES ACCUMULATE, NOT THE VALUES. See the header.
    acc = cumulative ? acc.concat(b.trades) : b.trades;
    if (split) {
      const m = metrics[0] as CustomMetric;
      points.push({
        label: b.label,
        values: {
          long: m.compute(acc.filter((t) => t.side === 'long')),
          short: m.compute(acc.filter((t) => t.side === 'short')),
        },
      });
    } else {
      const values: Record<string, number | null> = {};
      for (const m of metrics) values[m.id] = m.compute(acc);
      points.push({ label: b.label, values });
    }
  }

  const series: SeriesDef[] = split
    ? [
      { key: 'long', label: 'Long', color: LONG_COLOR, unit: metrics[0]!.unit, chart: chartAt(0) },
      { key: 'short', label: 'Short', color: SHORT_COLOR, unit: metrics[0]!.unit, chart: chartAt(0) },
    ]
    : metrics.map((m, i) => ({
      key: m.id,
      label: m.label,
      color: colors?.[i] || (CW_PALETTE[i % CW_PALETTE.length] as string),
      unit: m.unit,
      chart: chartAt(i),
    }));

  return { points, series };
}
